/**
 * Provider ngôn ngữ.
 *
 * Đổi ngôn ngữ phải đổi **bốn** thứ cùng lúc, nếu không giao diện lai hai thứ
 * tiếng ở những chỗ khó thấy nhất:
 *
 * 1. `i18next` — chuỗi của kho.
 * 2. `ConfigProvider locale` của antd — chuỗi sẵn có của thư viện (`Không có
 *    dữ liệu`, nhãn phân trang, nút của `DatePicker`). Việc này do
 *    `providers.tsx` làm, đọc `useNgonNgu()`.
 * 3. `dayjs.locale` — tên tháng và thứ.
 * 4. `<html lang>` — trình đọc màn hình đọc đúng ngữ âm. Thiếu bước này thì
 *    giọng đọc tiếng Việt đọc chữ Anh, nghe không ra chữ nào.
 *
 * Lưu ở `localStorage` như chủ đề, và `index.html` áp `lang` trước khi vẽ.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import dayjs from 'dayjs';

import { ghiNgonNgu, i18next } from './config';
import type { NgonNgu } from './config';

interface LanguageContextValue {
  ngonNgu: NgonNgu;
  setNgonNgu: (ma: NgonNgu) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * `dayjs` nạp locale bằng side effect nên phải import tĩnh cả hai từ đầu.
 * Nạp động (`import('dayjs/locale/en')`) thì lần đổi đầu tiên có một nhịp tên
 * tháng còn ở ngôn ngữ cũ.
 */
const DAYJS_LOCALE: Record<NgonNgu, string> = {
  vi: 'vi',
  en: 'en-gb',
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [ngonNgu, setState] = useState<NgonNgu>(() => i18next.language as NgonNgu);

  const setNgonNgu = useCallback((ma: NgonNgu) => {
    void i18next.changeLanguage(ma);
    dayjs.locale(DAYJS_LOCALE[ma]);
    ghiNgonNgu(ma);
    setState(ma);
  }, []);

  const value = useMemo(() => ({ ngonNgu, setNgonNgu }), [ngonNgu, setNgonNgu]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useNgonNgu(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useNgonNgu phải được gọi bên trong <LanguageProvider>');
  return ctx;
}
